import { Dialog, Transition } from "@headlessui/react"
import { clx } from "@modules/common/components/ui"
import React, { Fragment, useMemo } from "react"

import useToggleState from "@lib/hooks/use-toggle-state"
import ChevronDown from "@modules/common/icons/chevron-down"
import X from "@modules/common/icons/x"

import { getProductPrice } from "@lib/util/get-product-price"
import OptionSelect from "./option-select"
import { HttpTypes } from "@medusajs/types"
import { isSimpleProduct } from "@lib/util/product"
import { useT } from "@lib/i18n/use-t"

type MobileActionsProps = {
  product: HttpTypes.StoreProduct
  variant?: HttpTypes.StoreProductVariant
  options: Record<string, string | undefined>
  updateOptions: (title: string, value: string) => void
  inStock?: boolean
  handleAddToCart: () => void
  isAdding?: boolean
  show: boolean
  optionsDisabled: boolean
  quantity?: number
  locale?: string
}

const MobileActions: React.FC<MobileActionsProps> = ({
  product,
  variant,
  options,
  updateOptions,
  inStock,
  handleAddToCart,
  isAdding,
  show,
  optionsDisabled,
}) => {
  const { state, open, close } = useToggleState()
  const t = useT()

  const price = getProductPrice({
    product: product,
    variantId: variant?.id,
  })

  const selectedPrice = useMemo(() => {
    if (!price) {
      return null
    }
    const { variantPrice, cheapestPrice } = price

    return variantPrice || cheapestPrice || null
  }, [price])

  const isSimple = isSimpleProduct(product)

  return (
    <>
      <div
        className={clx("fixed inset-x-0 bottom-0 z-50 lg:hidden", {
          "pointer-events-none": !show,
        })}
      >
        <Transition
          as={Fragment}
          show={show}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0 translate-y-2"
          enterTo="opacity-100 translate-y-0"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-2"
        >
          <div
            className="flex h-full w-full flex-col gap-y-3 border-t border-slate-200/60 bg-white/95 p-4 shadow-[0_-12px_24px_-12px_rgba(15,23,42,0.15)] backdrop-blur dark:border-white/10 dark:bg-[#050816]/95"
            data-testid="mobile-actions"
          >
            <div className="flex items-center justify-between gap-3">
              <span
                className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900 dark:text-white"
                data-testid="mobile-title"
              >
                {product.title}
              </span>
              {selectedPrice && (
                <div className="flex shrink-0 items-baseline gap-2">
                  {selectedPrice.price_type === "sale" && (
                    <span className="text-xs text-slate-400 line-through">
                      {selectedPrice.original_price}
                    </span>
                  )}
                  <span className="text-base font-semibold text-slate-900 dark:text-white">
                    {selectedPrice.calculated_price}
                  </span>
                </div>
              )}
            </div>
            <div
              className={clx("grid w-full grid-cols-2 gap-3", {
                "!grid-cols-1": isSimple,
              })}
            >
              {!isSimple && (
                <button
                  type="button"
                  onClick={open}
                  data-testid="mobile-actions-button"
                  className="inline-flex h-11 w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-white/[0.02] dark:text-slate-200 dark:hover:bg-white/[0.05]"
                >
                  <span className="truncate">
                    {variant
                      ? Object.values(options).join(" / ")
                      : t("pdpSelectOptions")}
                  </span>
                  <ChevronDown />
                </button>
              )}
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={!inStock || !variant || isAdding}
                data-testid="mobile-cart-button"
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-brand-light px-4 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:bg-slate-300 dark:bg-brand-dark dark:disabled:bg-white/10 dark:disabled:text-slate-500"
              >
                {isAdding && (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  >
                    <path d="M21 12a9 9 0 11-9-9" strokeLinecap="round" />
                  </svg>
                )}
                {!variant
                  ? t("pdpSelectVariant")
                  : !inStock
                  ? t("pdpOutOfStock")
                  : t("pdpAddToCart")}
              </button>
            </div>
          </div>
        </Transition>
      </div>
      <Transition appear show={state} as={Fragment}>
        <Dialog as="div" className="relative z-[75]" onClose={close}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-x-0 bottom-0">
            <div className="flex h-full min-h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-4"
              >
                <Dialog.Panel
                  className="flex h-full w-full transform flex-col gap-y-3 overflow-hidden text-left"
                  data-testid="mobile-actions-modal"
                >
                  <div className="flex w-full justify-end pr-6">
                    <button
                      onClick={close}
                      className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm transition hover:bg-slate-50 dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
                      data-testid="close-modal-button"
                      aria-label="close"
                    >
                      <X />
                    </button>
                  </div>
                  <div className="rounded-t-2xl bg-white px-6 py-10 dark:bg-[#050816]">
                    {(product.variants?.length ?? 0) > 1 && (
                      <div className="flex flex-col gap-y-6">
                        {(product.options || []).map((option) => {
                          return (
                            <div key={option.id}>
                              <OptionSelect
                                option={option}
                                current={options[option.id]}
                                updateOption={updateOptions}
                                title={option.title ?? ""}
                                disabled={optionsDisabled}
                              />
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export default MobileActions
